import React from 'react';

import DevText from '@app/components/Text';
import { ReturnValue as TextArrayFormFieldReturnValue } from '@app/hooks/validation/useTextArrayFormField';
import { Button, Classes, FormGroup, Intent } from '@blueprintjs/core';
import Flex from '@components/Flex';

import { MAX_EXPECTED_DELIVERABLES, MAX_EXPECTED_DELIVERABLE_LENGTH } from '../../../utils';

export interface DeliverableListFieldProps {
    field: TextArrayFormFieldReturnValue<string[]>;
    disabled?: boolean;
}

export type Props = DeliverableListFieldProps;

// One management input rendered as a repeating row. Blueprint's `InputGroup` is unusable as a
// JSX component under this repo's React type versions, so the rows use a native input carrying
// Blueprint's own input class — visually identical, and it keeps this file type-clean.
const DeliverableListField: React.FC<Props> = ({ field, disabled = false }) => {
    const values = field.value;
    const isAddDisabled = disabled || values.length >= MAX_EXPECTED_DELIVERABLES;

    const handleChangeAt = (index: number, value: string) => {
        const next = values.slice();
        next[index] = value;
        field.handleChange(next);
    };

    const handleRemoveAt = (index: number) => {
        field.handleChange(values.filter((_, i) => i !== index));
    };

    const renderRow = (value: string, index: number) => (
        <Flex className={index === 0 ? '' : 'mt-1'} key={index} direction="row" align="center">
            <input
                className={Classes.INPUT}
                style={{ flex: 1 }}
                disabled={disabled}
                maxLength={MAX_EXPECTED_DELIVERABLE_LENGTH}
                placeholder="Describe an expected deliverable"
                value={value}
                onChange={(e) => handleChangeAt(index, e.target.value)}
                onBlur={() => field.handleBlur(index)}
            />

            <Button
                className="ml-1"
                minimal
                disabled={disabled}
                icon="cross"
                intent={Intent.DANGER}
                onClick={() => handleRemoveAt(index)}
            />
        </Flex>
    );

    return (
        <FormGroup
            intent={!!field.error ? Intent.DANGER : Intent.NONE}
            helperText={field.error}
            label="Expected deliverables"
        >
            {values.length === 0 && (
                <DevText muted className="mb-1">
                    No expected deliverables added yet
                </DevText>
            )}

            {values.map(renderRow)}

            <Button
                className="mt-1"
                minimal
                icon="plus"
                disabled={isAddDisabled}
                onClick={() => field.handleChange([...values, ''])}
            >
                Add deliverable
            </Button>

            {values.length >= MAX_EXPECTED_DELIVERABLES && (
                <DevText muted small className="mt-1">
                    Maximum of {MAX_EXPECTED_DELIVERABLES} deliverables reached
                </DevText>
            )}
        </FormGroup>
    );
};

export default DeliverableListField;
